import './styles/index.css';

import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Suspense } from 'react';
import { RouterProvider } from 'react-aria';
import { navigate } from 'vike/client/router';

import { ClientOnly } from '@/components/ClientOnly';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Alerts } from '@/components/UI/Alert';
import WebSocketWatcher from '@/components/UI/WebSocketWatcher';
import {
  TypingProvider,
  TypingStateUpdater,
} from '@/pages/(guarded)/support-chat/components/TypingContext';
import { usePageContext } from '@/renderer/utils/usePageContext';
// import Header from './Header';
import Nav from './Nav';
import { RasContextProvider } from './utils/RasContext';
import { InfiniteStoreProvider } from './utils/api/InfiniteStoreProvider';
import { useAddToHomescreenPrompt } from './utils/hooks/useAddToHomescreenPrompt';
import { ForceLogoutWatcher, PageTokenRefresher } from './utils/useMe';

export { PageLayout };

function useHref(href: string) {
  // if (!IS_SERVER && href?.startsWith) {
  //   href.startsWith(import meta url or perhaps location.origin? not sure);
  // should probably check if it is a local url
  // }

  return href;
}

function PageLayout({
  children,
}: {
  children: JSX.Element;
}) {
  const { urlPathname } = usePageContext();

  // Make sure we have this on every page so that the beforeinstall event is always bound
  useAddToHomescreenPrompt();

  return (
    <RasContextProvider>
      <div className="page-loading" />

      <TypingProvider>
        <RouterProvider navigate={navigate} useHref={useHref}>
          <section className="h-full">
            <InfiniteStoreProvider>
              <ErrorBoundary>
                <Suspense fallback={<Nav withoutUser={true} />}>
                  <Nav />
                </Suspense>
              </ErrorBoundary>

              <main
                data-url={urlPathname}
                className="flex h-full flex-col md:peer-first-of-type/nav:pl-72"
              >
                <ErrorBoundary
                  fallback={
                    <div className="flex shrink grow flex-col items-center justify-center">
                      <div className="my-auto flex flex-row items-center space-x-2 text-center text-sm">
                        <ExclamationCircleIcon height={24} className="size-6" />
                        <span>
                          There was a problem handling your session. Please{' '}
                          <a href="#" onClick={() => location.reload()}>
                            refresh
                          </a>{' '}
                          to try again.
                        </span>
                      </div>
                    </div>
                  }
                >
                  <Suspense>
                    <PageTokenRefresher />
                  </Suspense>
                </ErrorBoundary>

                <ErrorBoundary
                  fallback={
                    <div className="flex shrink grow flex-col items-center justify-center">
                      <div className="my-auto flex flex-row items-center space-x-2 text-center text-sm">
                        <ExclamationCircleIcon height={24} className="size-6" />
                        <span>
                          There was a problem setting user state. Please{' '}
                          <a href="#" onClick={() => location.reload()}>
                            refresh
                          </a>{' '}
                          to try again.
                        </span>
                      </div>
                    </div>
                  }
                >
                  <Suspense>
                    <ForceLogoutWatcher />
                  </Suspense>
                </ErrorBoundary>

                <ErrorBoundary
                  fallback={
                    <div className="flex shrink grow flex-col items-center justify-center">
                      <div className="my-auto flex flex-row items-center space-x-2 text-center text-sm">
                        <ExclamationCircleIcon height={24} className="size-6" />
                        <span>
                          There was a problem registering Web Sockets. Please{' '}
                          <a href="#" onClick={() => location.reload()}>
                            refresh
                          </a>{' '}
                          to try again.
                        </span>
                      </div>
                    </div>
                  }
                >
                  <Suspense>
                    <WebSocketWatcher />
                  </Suspense>
                </ErrorBoundary>

                <ErrorBoundary>
                  <Suspense>
                    <TypingStateUpdater />
                  </Suspense>
                </ErrorBoundary>

                <ErrorBoundary>
                  <Suspense>
                    <ClientOnly
                      component={() => import('@/components/UI/DesktopNotifications')}
                      isMain={true}
                    />
                  </Suspense>
                </ErrorBoundary>

                <ErrorBoundary>{children}</ErrorBoundary>
              </main>
            </InfiniteStoreProvider>
          </section>

          <Alerts />

          <ErrorBoundary>
            <Suspense>
              <ClientOnly component={() => import('@/renderer/utils/sw/register')} />
            </Suspense>
          </ErrorBoundary>
        </RouterProvider>
      </TypingProvider>
    </RasContextProvider>
  );
}
